import { create } from "zustand";

export type SectionInView = {
	id: string;
	label: string;
	order: number;
	inView: boolean;
	intersectionRatio: number;
	bounds?: {
		top: number;
		height: number;
	};
};

type RegisterSectionPayload = Pick<SectionInView, "id" | "label" | "order">;

type UpdateSectionVisibilityPayload = Pick<
	SectionInView,
	"id" | "inView" | "intersectionRatio"
> & {
	bounds?: { top: number; height: number };
};

export type InViewStore = {
	sectionsInView: SectionInView[];
	registerSection: (section: RegisterSectionPayload) => void;
	unregisterSection: (sectionId: string) => void;
	updateSectionVisibility: (section: UpdateSectionVisibilityPayload) => void;
	getOrderedSections: () => SectionInView[];
};

export const useInViewStore = create<InViewStore>((set, get) => ({
	sectionsInView: [],
	registerSection: (section) =>
		set((state) => {
			const alreadyRegistered = state.sectionsInView.some(
				(existing) => existing.id === section.id,
			);

			if (alreadyRegistered) {
				return {
					sectionsInView: state.sectionsInView.map((existing) =>
						existing.id === section.id
							? {
									...existing,
									label: section.label,
									order: section.order,
							  }
							: existing,
					),
				};
			}

			return {
				sectionsInView: [
					...state.sectionsInView,
					{
						...section,
						inView: false,
						intersectionRatio: 0,
					},
				],
			};
		}),
	unregisterSection: (sectionId) =>
		set((state) => ({
			sectionsInView: state.sectionsInView.filter(
				(section) => section.id !== sectionId,
			),
		})),

	updateSectionVisibility: (section) =>
		set((state) => ({
			sectionsInView: state.sectionsInView.map((existing) =>
				existing.id === section.id
					? {
							...existing,
							inView: section.inView,
							intersectionRatio: section.intersectionRatio,
							bounds: section.bounds ?? existing.bounds,
					  }
					: existing,
			),
		})),

	getOrderedSections: () => {
		const currentSections = get().sectionsInView;
		return [...currentSections].sort((a, b) => a.order - b.order);
	},
}));
